import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import * as FaIcons from "react-icons/fa";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import Loader from "../Loader/Loader";
import {
  buildInvoiceBrandingPayload,
  emptyInvoiceBrandingForm,
  getInvoiceBrandingApiMessage,
  invoiceBrandingTitleFields,
  invoiceBrandingRequiredFields,
  isInvoiceBrandingApiSuccess,
  mapRowToInvoiceBrandingForm,
  normalizeInvoiceBrandingList,
} from "../../utilis/invoiceBrandingHelpers";
import { invalidateInvoiceCatalog } from "../../utilis/invoiceWorkspaceApi";

const sectionLabelStyle = {
  fontWeight: 600,
  color: "#374151",
  fontSize: "0.9rem",
  marginBottom: "0.75rem",
};

const titleRowStyle = {
  display: "grid",
  gridTemplateColumns: "170px 1fr",
  alignItems: "center",
  gap: "0.75rem",
  marginBottom: "0.65rem",
};

function RequiredLabel({ children, htmlFor, className = "" }) {
  return (
    <label className={`form-label mb-0 ${className}`} htmlFor={htmlFor}>
      {children}
      <span className="text-danger ms-1">*</span>
    </label>
  );
}

function FieldError({ message }) {
  if (!message) return null;
  return <div className="text-danger small mt-1">{message}</div>;
}

function BrandingInfoSection({ label, value, required = false }) {
  const text = String(value ?? "").trim();

  return (
    <div className="branding-theme-card__section">
      <div className="branding-theme-card__section-label">
        {label}
        {required && <span className="text-danger ms-1">*</span>}
      </div>
      <div className="branding-theme-card__section-value">
        {text || <span className="text-muted">—</span>}
      </div>
    </div>
  );
}

function BrandingThemeCard({ row, onEdit, onCopy, onDelete }) {
  return (
    <div className="branding-theme-card h-100">
      <div className="branding-theme-card__content">
        <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
          <div className="min-w-0">
            <div className="branding-theme-card__label">Name</div>
            <h3 className="branding-theme-card__name mb-0">
              {row.name || "Untitled theme"}
            </h3>
          </div>
          <div className="branding-theme-card__actions">
            <button
              type="button"
              className="btn btn-sm btn-light branding-theme-card__action-btn"
              onClick={() => onCopy(row)}
              title="Copy theme"
            >
              <FaIcons.FaCopy />
            </button>
            <button
              type="button"
              className="btn btn-sm btn-light branding-theme-card__action-btn"
              onClick={() => onEdit(row)}
              title="Edit theme"
            >
              <FaIcons.FaPencilAlt />
            </button>
            <button
              type="button"
              className="btn btn-sm btn-light branding-theme-card__action-btn text-danger"
              onClick={() => onDelete(row)}
              title="Delete theme"
            >
              <FaIcons.FaTrashAlt />
            </button>
          </div>
        </div>

        <div className="branding-theme-card__info">
          <BrandingInfoSection
            label="Company details"
            value={row.contact_details}
            required
          />
          <BrandingInfoSection
            label="Payment advice"
            value={row.payment_advice}
            required
          />
          <BrandingInfoSection
            label="Terms & conditions"
            value={row.terms_conditions}
            required
          />
        </div>
      </div>
    </div>
  );
}

function InvoiceBranding() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchByName, setSearchByName] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [copySource, setCopySource] = useState(null);
  const [formData, setFormData] = useState(emptyInvoiceBrandingForm);
  const [formErrors, setFormErrors] = useState({});

  const clearFormErrors = () => setFormErrors({});

  const fetchBrandings = async (name = searchByName) => {
    setLoading(true);
    try {
      const trimmedName = String(name ?? "").trim();
      const response = await apiUrl.get("getAllInvoiceBrandings", {
        params: trimmedName ? { searchByName: trimmedName } : {},
      });

      if (!isInvoiceBrandingApiSuccess(response)) {
        throw new Error(
          getInvoiceBrandingApiMessage(
            response,
            "Could not load invoice branding.",
          ),
        );
      }

      setRows(normalizeInvoiceBrandingList(response));
    } catch (error) {
      Swal.fire(
        "Failed",
        error?.response?.data?.message ||
          error?.message ||
          "Could not load invoice branding.",
        "error",
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchByName(searchInput.trim());
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchBrandings(searchByName);
  }, [searchByName]);

  const updateField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setFormErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const openCreateModal = () => {
    setEditRow(null);
    setCopySource(null);
    setFormData({ ...emptyInvoiceBrandingForm });
    clearFormErrors();
    setShowModal(true);
  };

  const openEditModal = (row) => {
    setEditRow(row);
    setCopySource(null);
    setFormData(mapRowToInvoiceBrandingForm(row));
    clearFormErrors();
    setShowModal(true);
  };

  const openCopyModal = (row) => {
    setEditRow(null);
    setCopySource(row);
    setFormData(mapRowToInvoiceBrandingForm(row));
    clearFormErrors();
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setEditRow(null);
    setCopySource(null);
    clearFormErrors();
  };

  const validate = () => {
    const errors = {};
    let isValid = true;

    invoiceBrandingRequiredFields.forEach((field) => {
      if (!String(formData[field.key] ?? "").trim()) {
        errors[field.key] = `${field.label} is required.`;
        isValid = false;
      }
    });

    setFormErrors(errors);
    return isValid;
  };

  const handleSave = async () => {
    if (!validate() || saving) return;

    setSaving(true);
    const payload = buildInvoiceBrandingPayload(formData);

    try {
      const response = editRow
        ? await apiUrl.post(`updateInvoiceBranding/${editRow.id}`, payload)
        : await apiUrl.post("createInvoiceBranding", payload);

      if (!isInvoiceBrandingApiSuccess(response)) {
        throw new Error(
          getInvoiceBrandingApiMessage(
            response,
            `Unable to ${editRow ? "update" : "create"} invoice branding.`,
          ),
        );
      }

      invalidateInvoiceCatalog();
      Swal.fire(
        "Success",
        getInvoiceBrandingApiMessage(
          response,
          `Invoice branding ${editRow ? "updated" : "created"} successfully.`,
        ),
        "success",
      );
      setShowModal(false);
      setEditRow(null);
      setCopySource(null);
      fetchBrandings();
    } catch (error) {
      Swal.fire(
        "Failed",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to save invoice branding.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const deleteBranding = async (row) => {
    const confirmation = await Swal.fire({
      title: "Delete branding theme?",
      text: `Remove "${row.name || "selected theme"}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
    });

    if (!confirmation.isConfirmed) return;

    try {
      const response = await apiUrl.delete(`deleteInvoiceBranding/${row.id}`);

      if (!isInvoiceBrandingApiSuccess(response)) {
        throw new Error(
          getInvoiceBrandingApiMessage(
            response,
            "Unable to delete invoice branding.",
          ),
        );
      }

      invalidateInvoiceCatalog();
      Swal.fire(
        "Deleted",
        getInvoiceBrandingApiMessage(response, "Invoice branding removed."),
        "success",
      );
      fetchBrandings();
    } catch (error) {
      Swal.fire(
        "Failed",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to delete invoice branding.",
        "error",
      );
    }
  };

  return (
    <>
      <div className="container-fluid py-4 pb-5">
        <div className="container">
          <div
            className="card mb-4 shadow-sm border-0"
            style={{ background: "linear-gradient(90deg, #ffffff, #d2e8f8)" }}
          >
            <div className="card-body d-flex align-items-center justify-content-between gap-3 flex-wrap">
              <div className="d-flex align-items-center">
                <div
                  className="me-3 d-flex justify-content-center align-items-center rounded-circle"
                  style={{ width: 50, height: 50, backgroundColor: "#435690" }}
                >
                  <FaIcons.FaPalette className="text-white fs-4" />
                </div>
                <div>
                  <h1 className="h3 mb-1 fw-bold text-dark">
                    Invoice Branding
                  </h1>
                  <p className="text-muted mb-0">
                    Manage branding themes for invoices, receipts, and
                    statements.
                  </p>
                </div>
              </div>
              <button className="btn btn-primary" onClick={openCreateModal}>
                <FaIcons.FaPlus className="me-2" />
                Add Branding Theme
              </button>
            </div>
          </div>

          <div className="mb-4">
            <div className="row g-3 align-items-center">
              <div className="col-md-6 col-lg-5">
                <div className="branding-search-wrap">
                  <FaIcons.FaSearch className="branding-search-icon" />
                  <input
                    type="text"
                    className="form-control branding-search-input"
                    placeholder="Search by name..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-md-6 col-lg-7 text-md-end">
                <span className="text-muted small">
                  {rows.length} {rows.length === 1 ? "theme" : "themes"}
                </span>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="branding-state-panel">
              <Loader />
            </div>
          ) : rows.length === 0 && !searchByName ? (
            <div className="branding-state-panel">
              <div className="branding-empty-state">
                <div className="branding-empty-state__icon">
                  <FaIcons.FaPalette />
                </div>
                <h4 className="mb-2">No branding themes yet</h4>
                <p className="text-muted mb-4">
                  Create your first theme to customise document titles, contact
                  details, and payment advice on invoices.
                </p>
                <button className="btn btn-primary" onClick={openCreateModal}>
                  <FaIcons.FaPlus className="me-2" />
                  Add Branding Theme
                </button>
              </div>
            </div>
          ) : rows.length === 0 ? (
            <div className="branding-state-panel">
              <p className="text-muted mb-0">
                No branding themes match your search.
              </p>
            </div>
          ) : (
            <div className="branding-cards-grid mb-5">
              {rows.map((row, index) => (
                <BrandingThemeCard
                  key={row.id || index}
                  row={row}
                  onEdit={openEditModal}
                  onCopy={openCopyModal}
                  onDelete={deleteBranding}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{
              display: "block",
              backgroundColor: "rgba(0,0,0,0.45)",
              zIndex: 1040,
            }}
            onClick={closeModal}
          />
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            role="dialog"
            style={{ zIndex: 1050 }}
          >
            <div className="modal-dialog modal-dialog-centered modal-xl">
              <div className="modal-content border-0 shadow rounded-3 overflow-hidden">
                <div
                  className="modal-header py-3"
                  style={{
                    background: "#f7f8fa",
                    borderBottom: "1px solid #eef2f6",
                  }}
                >
                  <h5 className="modal-title fw-bold mb-0">
                    {editRow
                      ? "Edit Branding Theme"
                      : copySource
                        ? "Copy Branding Theme"
                        : "Add Branding Theme"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeModal}
                    disabled={saving}
                  />
                </div>

                <div className="modal-body p-4" style={{ background: "#fff" }}>
                  <div className="mb-4" style={titleRowStyle}>
                    <RequiredLabel
                      htmlFor="branding_name"
                      className="fw-semibold text-secondary"
                    >
                      Name
                    </RequiredLabel>
                    <div>
                      <input
                        id="branding_name"
                        type="text"
                        className={`form-control ${
                          formErrors.name ? "is-invalid" : ""
                        }`}
                        value={formData.name}
                        onChange={(e) => updateField("name", e.target.value)}
                        placeholder="e.g. Visa Clients"
                      />
                      <FieldError message={formErrors.name} />
                    </div>
                  </div>

                  <div className="row g-4">
                    <div className="col-lg-6">
                      <div style={sectionLabelStyle}>Document Titles</div>
                      <div
                        className="p-3 rounded-3"
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #e5e7eb",
                        }}
                      >
                        {invoiceBrandingTitleFields.map((field) => (
                          <div key={field.key} style={titleRowStyle}>
                            <RequiredLabel
                              htmlFor={field.key}
                              className="small text-secondary"
                            >
                              {field.label}
                            </RequiredLabel>
                            <div>
                              <input
                                id={field.key}
                                type="text"
                                className={`form-control form-control-sm ${
                                  formErrors[field.key] ? "is-invalid" : ""
                                }`}
                                value={formData[field.key]}
                                onChange={(e) =>
                                  updateField(field.key, e.target.value)
                                }
                              />
                              <FieldError message={formErrors[field.key]} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div style={sectionLabelStyle}>Contact & Terms</div>
                      <div
                        className="p-3 rounded-3 h-100"
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #e5e7eb",
                        }}
                      >
                        <div className="mb-3">
                          <RequiredLabel className="small fw-semibold text-secondary">
                            Company details
                          </RequiredLabel>
                          <p className="text-muted small mb-2">
                            Enter the Company details that will be displayed on
                            all invoices generated, printed, or sent by the
                            system.{" "}
                          </p>
                          <textarea
                            className={`form-control ${
                              formErrors.contact_details ? "is-invalid" : ""
                            }`}
                            rows={5}
                            value={formData.contact_details}
                            onChange={(e) =>
                              updateField("contact_details", e.target.value)
                            }
                            placeholder="Address, phone, email..."
                          />
                          <FieldError message={formErrors.contact_details} />
                        </div>

                        <div className="mb-3">
                          <RequiredLabel className="small fw-semibold text-secondary">
                            Payment advice
                          </RequiredLabel>
                          <p className="text-muted small mb-2">
                            Terms & Payment Advice (Invoice and Statement)
                          </p>
                          <textarea
                            className={`form-control ${
                              formErrors.payment_advice ? "is-invalid" : ""
                            }`}
                            rows={5}
                            value={formData.payment_advice}
                            onChange={(e) =>
                              updateField("payment_advice", e.target.value)
                            }
                            placeholder="Account name, BSB, account number..."
                          />
                          <FieldError message={formErrors.payment_advice} />
                        </div>

                        <div>
                          <RequiredLabel className="small fw-semibold text-secondary">
                            Terms & conditions
                          </RequiredLabel>
                          <p className="text-muted small mb-2">
                            Terms shown on quotes and invoices.
                          </p>
                          <textarea
                            className={`form-control ${
                              formErrors.terms_conditions ? "is-invalid" : ""
                            }`}
                            rows={5}
                            value={formData.terms_conditions}
                            onChange={(e) =>
                              updateField("terms_conditions", e.target.value)
                            }
                            placeholder="Enter terms and conditions..."
                          />
                          <FieldError message={formErrors.terms_conditions} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="modal-footer py-3"
                  style={{
                    background: "#f7f8fa",
                    borderTop: "1px solid #eef2f6",
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-4"
                    onClick={closeModal}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-success px-4"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving
                      ? "Saving..."
                      : editRow
                        ? "Save"
                        : copySource
                          ? "Create copy"
                          : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <style>{`
        .branding-search-wrap {
          position: relative;
        }
        .branding-search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
          font-size: 0.9rem;
          pointer-events: none;
        }
        .branding-search-input {
          padding-left: 2.5rem;
          border-radius: 10px;
          border: 1px solid #e5e7eb;
          background: #fff;
          box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
        }
        .branding-search-input:focus {
          border-color: #93c5fd;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
        }
        .branding-state-panel {
          min-height: 280px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #fff;
          border: 1px solid #e8edf3;
          border-radius: 16px;
          padding: 2.5rem 1.5rem;
          margin-bottom: 2rem;
        }
        .branding-empty-state {
          text-align: center;
          max-width: 420px;
        }
        .branding-empty-state__icon {
          width: 64px;
          height: 64px;
          margin: 0 auto 1rem;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #eef4ff, #dbeafe);
          color: #435690;
          font-size: 1.5rem;
        }
        .branding-cards-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
        }
        @media (min-width: 992px) {
          .branding-cards-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 2rem 2.5rem;
          }
        }
        @media (min-width: 1200px) {
          .branding-cards-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }
        .branding-theme-card {
          background: #fff;
          border: 1px solid #e4e9f0;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 14px rgba(16, 24, 40, 0.06);
          transition: transform 0.18s ease, box-shadow 0.18s ease;
        }
        .branding-theme-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 24px rgba(16, 24, 40, 0.1);
        }
        .branding-theme-card__content {
          padding: 1.15rem 1.2rem 1.2rem;
        }
        .branding-theme-card__label {
          font-size: 0.72rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #6b7280;
          margin-bottom: 0.2rem;
        }
        .branding-theme-card__name {
          font-size: 1.05rem;
          font-weight: 700;
          color: #111827;
          line-height: 1.35;
          min-width: 0;
        }
        .branding-theme-card__info {
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
        }
        .branding-theme-card__section {
          padding: 0.75rem 0.85rem;
          background: #f8fafc;
          border: 1px solid #e8edf3;
          border-radius: 10px;
        }
        .branding-theme-card__section-label {
          font-size: 0.78rem;
          font-weight: 600;
          color: #435690;
          margin-bottom: 0.4rem;
        }
        .branding-theme-card__section-value {
          font-size: 0.82rem;
          color: #374151;
          line-height: 1.5;
          white-space: pre-line;
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
          word-break: break-word;
        }
        .branding-theme-card__actions {
          display: flex;
          gap: 0.35rem;
          flex-shrink: 0;
        }
        .branding-theme-card__action-btn {
          width: 32px;
          height: 32px;
          padding: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }
      `}</style>
    </>
  );
}

export default InvoiceBranding;
